import { z } from 'zod'
import { type Expand } from '@/types/ui/expand'
import { ENGAGEMENT_BILLING_TYPE, ENGAGEMENT_TYPE } from '@/lib/constants/engagement-codes'

// Base schema for task form data
const baseTaskSchema = z.object({
  id: z.number(),
  name: z.string().min(1, 'Field is required').max(100, 'Field must be less than 100 characters'),
  billable: z.boolean().default(true),
  inactive: z.boolean().default(false),
  isUtilized: z.boolean().optional(),
})

// Default schema for task form
export const taskFormSchema = baseTaskSchema

// Function to create task form array schema with conditional validation
export const createTaskFormArraySchema = (billingType?: string, engagementType?: string) => {
  return z.object({
    tasks: z.array(baseTaskSchema)
      .min(1, 'At least one task is required')
      .superRefine((tasks, ctx) => {
        // Ensure at least one task is not inactive (active)
        const activeTasks = tasks.filter(task => !task.inactive)
        if (activeTasks.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one active task is required',
            path: []
          })
        }
        
        // Validate billable tasks are not allowed for internal engagements or milestone billing
        const isBillableDisallowed = engagementType === ENGAGEMENT_TYPE.INTERNAL || billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE
        if (isBillableDisallowed) {
          const billableTasks = tasks.filter(task => task.billable)
          if (billableTasks.length > 0) {
            const message = engagementType === ENGAGEMENT_TYPE.INTERNAL
              ? 'Internal engagements cannot have billable tasks. Convert to non-billable or remove them.'
              : 'Milestone billing engagements cannot have billable tasks. Convert to non-billable or remove them.'
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message,
              path: []
            })
          }
        }
      })
  })
}

// Schema for the task form array
export const taskFormArraySchema = createTaskFormArraySchema()

// Automatically infer the TypeScript type from the schema
export type TaskFormData = Expand<z.infer<typeof taskFormSchema>>
export type TaskFormArrayData = Expand<z.infer<typeof taskFormArraySchema>> 